import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { projectsApi } from '@/api/projects'
import { useEffectiveRole } from '@/store/auth'
import StatusBadge from '@/components/projects/StatusBadge'
import LifecycleBadge from '@/components/projects/LifecycleBadge'
import Spinner from '@/components/common/Spinner'
import NoProjectsPage from '@/pages/NoProjectsPage'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { Search, Plus, Trash2, RotateCcw, Archive } from 'lucide-react'
import type { ProjectListItem } from '@/types'
import HelpButton from '@/components/common/HelpButton'
import ConfirmModal from '@/components/common/ConfirmModal'
import TabBtn from '@/components/common/TabBtn'

type ListTab = 'active' | 'completed' | 'archived' | 'deleted'

function fmtDate(iso: string) {
  return format(new Date(iso), 'dd.MM.yyyy', { locale: ru })
}

export default function ProjectsPage() {
  const effectiveRole = useEffectiveRole()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [search, setSearch]               = useState('')
  const [tab, setTab]                     = useState<ListTab>('active')
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [confirmHard, setConfirmHard]     = useState<string | null>(null)

  const isDirectorFamily = effectiveRole === 'director' || effectiveRole === 'deputy_director'

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list().then((r) => r.data),
    // Чужие правки (сроки, статусы) подтягиваются фоном без перезагрузки
    refetchInterval: 20_000,
  })

  const { data: deletedProjects = [], isLoading: loadingDeleted } = useQuery({
    queryKey: ['projects-deleted'],
    queryFn: () => projectsApi.listDeleted().then((r) => r.data),
    enabled: isDirectorFamily && tab === 'deleted',
  })

  const softDeleteMutation = useMutation({
    mutationFn: (id: string) => projectsApi.deleteProject(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      qc.invalidateQueries({ queryKey: ['projects-deleted'] })
      setConfirmDelete(null)
    },
  })

  const restoreMutation = useMutation({
    mutationFn: (id: string) => projectsApi.restoreProject(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      qc.invalidateQueries({ queryKey: ['projects-deleted'] })
    },
  })

  const unarchiveMutation = useMutation({
    mutationFn: (id: string) => projectsApi.unarchive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => projectsApi.hardDeleteProject(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects-deleted'] })
      setConfirmHard(null)
    },
  })

  const { active, completed, archived } = useMemo(() => {
    const q = search.toLowerCase()
    const match = (p: ProjectListItem) =>
      q === '' || p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q)

    const active = projects
      .filter((p) => !p.is_archived && p.lifecycle_stage !== 'post_design' && match(p))
      .sort((a, b) => new Date(a.planned_end_date).getTime() - new Date(b.planned_end_date).getTime())

    const completed = projects
      .filter((p) => !p.is_archived && p.lifecycle_stage === 'post_design' && match(p))
      .sort((a, b) => new Date(b.planned_end_date).getTime() - new Date(a.planned_end_date).getTime())

    // Заархивированные — не попадают ни в «Активные», ни в «Завершённые» (архив вне жизненного цикла)
    const archived = projects
      .filter((p) => p.is_archived && match(p))
      .sort((a, b) => new Date(b.planned_end_date).getTime() - new Date(a.planned_end_date).getTime())

    return { active, completed, archived }
  }, [projects, search])

  if (isLoading) return <Spinner size="lg" />

  // Проектировщик без активных проектов → страница с игрой
  if (effectiveRole === 'designer' && !isLoading && active.length === 0 && search === '' && tab === 'active') {
    return <NoProjectsPage />
  }

  const displayed = tab === 'active' ? active : tab === 'completed' ? completed : archived

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between" data-tour="new-project-btn">
        <h1 className="text-xl font-semibold text-gray-900">Проекты</h1>
        <div className="flex items-center gap-2">
          <HelpButton />
          {isDirectorFamily && tab === 'active' && (
            <Link
              to="/projects/new"
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Новый проект
            </Link>
          )}
        </div>
      </div>

      {/* Вкладки */}
      <div className="flex items-center gap-1 border-b border-gray-200" data-tour="projects-tabs">
        <TabBtn label="Активные"    count={active.length}    active={tab === 'active'}    onClick={() => setTab('active')} />
        <TabBtn label="Завершённые" count={completed.length} active={tab === 'completed'} onClick={() => setTab('completed')} emerald />
        {isDirectorFamily && (
          <TabBtn label="Архив" count={archived.length} active={tab === 'archived'} onClick={() => setTab('archived')} />
        )}
        {isDirectorFamily && (
          <TabBtn label="Удалённые" count={deletedProjects.length} active={tab === 'deleted'} onClick={() => setTab('deleted')} danger />
        )}
      </div>

      {/* ── Таб «Удалённые» ──────────────────────────────────────────────────── */}
      {tab === 'deleted' && (
        <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
          {loadingDeleted ? (
            <div className="p-8 flex justify-center"><Spinner /></div>
          ) : deletedProjects.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">Корзина пуста</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Проект</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Заказчик</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Удалён</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {deletedProjects.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-gray-400 mr-2">{p.code}</span>
                      <span className="font-medium text-gray-700">{p.name}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-sm truncate max-w-xs">{p.customer}</td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      {p.deleted_at ? fmtDate(p.deleted_at) : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => restoreMutation.mutate(p.id)}
                          disabled={restoreMutation.isPending}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                        >
                          <RotateCcw className="h-3 w-3" />
                          Восстановить
                        </button>
                        <button
                          onClick={() => setConfirmHard(p.id)}
                          className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-2.5 py-1 text-xs text-rose-600 hover:bg-rose-50 transition-colors"
                        >
                          <Trash2 className="h-3 w-3" />
                          Удалить навсегда
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Активные / Завершённые ────────────────────────────────────────────── */}
      {tab !== 'deleted' && (
        <>
          {/* Поиск */}
          <div className="relative max-w-sm" data-tour="project-search">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по названию или шифру..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          {/* Таблица */}
          <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Шифр</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Название</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Заказчик</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Этап</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Состояние</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Срок</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {displayed.map((p) => (
                  <ProjectRow
                    key={p.id}
                    project={p}
                    canDelete={isDirectorFamily}
                    isCompleted={tab === 'completed'}
                    isArchived={tab === 'archived'}
                    onDelete={() => setConfirmDelete(p.id)}
                    onUnarchive={() => unarchiveMutation.mutate(p.id)}
                    unarchivePending={unarchiveMutation.isPending}
                  />
                ))}
                {displayed.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                      {search
                        ? 'Нет проектов по запросу'
                        : tab === 'completed'
                          ? 'Завершённых проектов нет'
                          : tab === 'archived'
                            ? 'Архив пуст'
                            : 'Активных проектов нет'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Модал подтверждения мягкого удаления */}
      {confirmDelete && (
        <ConfirmModal
          title="Переместить в корзину?"
          text="Проект будет скрыт из списка, но все данные сохранятся. Можно восстановить в любой момент."
          confirmLabel="В корзину"
          onConfirm={() => softDeleteMutation.mutate(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
          isPending={softDeleteMutation.isPending}
        />
      )}

      {/* Модал безвозвратного удаления */}
      {confirmHard && (
        <ConfirmModal
          title="Удалить навсегда?"
          text="Проект и все его разделы будут удалены безвозвратно. Это действие нельзя отменить."
          confirmLabel="Удалить навсегда"
          danger
          onConfirm={() => hardDeleteMutation.mutate(confirmHard)}
          onCancel={() => setConfirmHard(null)}
          isPending={hardDeleteMutation.isPending}
        />
      )}
    </div>
  )
}

// ─── ProjectRow ───────────────────────────────────────────────────────────────
function ProjectRow({
  project,
  canDelete,
  isCompleted,
  isArchived,
  onDelete,
  onUnarchive,
  unarchivePending,
}: {
  project: ProjectListItem
  canDelete?: boolean
  isCompleted?: boolean
  isArchived?: boolean
  onDelete?: () => void
  onUnarchive?: () => void
  unarchivePending?: boolean
}) {
  const end   = new Date(project.planned_end_date)
  const color = project.color ?? 'grey'

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 font-mono text-xs text-gray-600">{project.code}</td>
      <td className="px-4 py-3">
        <Link to={`/projects/${project.id}`} className="font-medium text-brand-600 hover:text-brand-700">
          {project.name}
        </Link>
      </td>
      <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{project.customer}</td>

      <td className="px-4 py-3">
        <LifecycleBadge stage={project.lifecycle_stage} />
      </td>

      <td className="px-4 py-3">
        {isArchived ? (
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
            <Archive className="h-3 w-3" />
            Архив
          </span>
        ) : isCompleted ? (
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            Завершён
          </span>
        ) : (
          <div className="flex items-center gap-1">
            <StatusBadge color={color} />
            {project.manual_color && (
              <span title="Задано вручную" className="text-xs text-gray-300">✋</span>
            )}
          </div>
        )}
      </td>

      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
        {format(end, 'dd.MM.yyyy')}
      </td>

      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          {isArchived && onUnarchive && (
            <button
              onClick={(e) => { e.preventDefault(); onUnarchive() }}
              disabled={unarchivePending}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              title="Вернуть из архива в активные"
            >
              <RotateCcw className="h-3 w-3" />
              Восстановить
            </button>
          )}
          {!isArchived && canDelete && onDelete && (
            <button
              onClick={(e) => { e.preventDefault(); onDelete() }}
              className="rounded p-1 text-gray-300 hover:text-rose-500 hover:bg-rose-50 transition-colors"
              title="В корзину"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          <Link
            to={`/projects/${project.id}`}
            title="Открыть"
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            →
          </Link>
        </div>
      </td>
    </tr>
  )
}
